import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import analyticsService from "../AnalyticsService";
import { useSelector } from "react-redux";
import {
  resolvePageCategory,
  resolvePageTitle,
  sanitizePageLocation,
} from "./resolvePageName";
import { resolveDashboardDimension } from "./resolveDashboardDimension";
import { toAnalyticsId } from "./interactionMetadata";
import engagementTracker from "./engagementTracker";
import { ANALYTICS_EVENTS } from "../constants";

export const usePageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastTrackedRouteKey = useRef(null);
  const previousPage = useRef({
    page_title: null,
    page_location: document?.referrer || null,
  });
  const lastDashboardTitle = useRef(null);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const isLoggedIn = useSelector((state) =>
    Boolean(state?.authReducer?.isLogin),
  );
  const currentPath = `${location?.pathname}${location?.search || ""}`;
  const currentPageLocation = sanitizePageLocation(
    `${globalThis?.location?.origin || ""}${currentPath}`,
  );
  const currentDashboardTitle =
    currentDashboard?.CONFIG?.pageHeader?.pageTitle || null;
  const dashboardDimension = resolveDashboardDimension(
    currentDashboard,
    currentPath,
  );
  const trackingRouteKey = location?.key || currentPageLocation;

  useEffect(() => {
    if (lastTrackedRouteKey.current === trackingRouteKey) {
      return undefined;
    }

    let timeoutId = null;
    let loadHandler = null;

    const runTracking = () => {
      lastTrackedRouteKey.current = trackingRouteKey;

      // page_title/page_location identify the page being viewed; the
      // previous_* values and page_referrer describe the in-app journey.
      const pageViewPayload = {
        page_title:
          resolvePageTitle(currentPath) || document?.title || "Application Page",
        page_category: resolvePageCategory(currentPath),
        page_location: currentPageLocation,
        page_referrer: previousPage.current.page_location,
        previous_page_title: previousPage.current.page_title,
        previous_page_location: previousPage.current.page_title
          ? previousPage.current.page_location
          : null,
        navigation_type: navigationType,
        ...(isLoggedIn && { dashboard: dashboardDimension }),
      };

      engagementTracker.startPage(pageViewPayload);
      analyticsService.trackPageView(pageViewPayload);
      previousPage.current = {
        page_title: pageViewPayload.page_title,
        page_location: currentPageLocation,
      };
    };

    const scheduleTracking = () => {
      if (document.readyState === "complete") {
        timeoutId = window.setTimeout(runTracking, 150);
        return;
      }

      loadHandler = () => {
        timeoutId = window.setTimeout(runTracking, 150);
      };

      window.addEventListener("load", loadHandler, { once: true });
    };

    scheduleTracking();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
    };
  }, [
    trackingRouteKey,
    currentPath,
    currentPageLocation,
    navigationType,
    currentDashboardTitle,
    dashboardDimension,
    isLoggedIn,
  ]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !currentDashboardTitle ||
      lastDashboardTitle.current === dashboardDimension
    ) {
      return;
    }

    lastDashboardTitle.current = dashboardDimension;
    analyticsService.track(
      ANALYTICS_EVENTS.SELECT_CONTENT,
      {
        content_type: "dashboard",
        content_id: `dashboard_${toAnalyticsId(dashboardDimension)}`,
        dashboard: dashboardDimension,
        element_id: `dashboard_${toAnalyticsId(dashboardDimension)}`,
        element_label: `Select ${dashboardDimension}`,
        element_type: "dashboard",
      },
      { nativeInteraction: false },
    );
  }, [currentDashboardTitle, dashboardDimension, isLoggedIn]);
};
